import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/useAuthStore';
import api from '../services/api';
import useToastStore from '../store/useToastStore';

const BENEFITS = [
  'Get a verified badge on your profile and listings',
  'Rank higher in search results and recommendations',
  'Build client trust and increase your conversion rate',
];

export default function VerificationPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [idFront, setIdFront] = useState(null);
  const [idBack, setIdBack] = useState(null);
  const [portfolioFiles, setPortfolioFiles] = useState([]);
  const [portfolioUrl, setPortfolioUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const idFrontRef = useRef(null);
  const idBackRef = useRef(null);
  const portfolioRef = useRef(null);

  const handlePortfolioFiles = (e) => {
    const files = Array.from(e.target.files || []);
    setPortfolioFiles((prev) => {
      const merged = [...prev, ...files];
      return merged.slice(0, 5);
    });
    e.target.value = '';
  };

  const handleSubmit = async () => {
    if (!idFront) { setError('Please upload the front of your government ID.'); return; }
    setError('');
    setSubmitting(true);
    try {
      const fd = new FormData();
      if (idFront) fd.append('idFront', idFront);
      if (idBack) fd.append('idBack', idBack);
      portfolioFiles.forEach((f) => fd.append('portfolio', f));
      if (portfolioUrl.trim()) fd.append('portfolioUrl', portfolioUrl.trim());
      await api.post('/users/me/verify', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Submission failed. Please try again.');
    }
    setSubmitting(false);
  };

  // Already verified screen
  if (user?.isVerified) {
    return (
      <div className="min-h-screen bg-dark-bg flex flex-col items-center justify-center px-6 pb-24 pt-12">
        <div className="w-20 h-20 rounded-full bg-green-500/20 border-2 border-green-400 flex items-center justify-center text-4xl mb-5">
          ✅
        </div>
        <h2 className="text-dark-text text-2xl font-bold mb-2" style={{ letterSpacing: '-0.5px' }}>
          You're Verified!
        </h2>
        <p className="text-dark-subtext text-sm text-center leading-relaxed mb-8">
          Your account has been verified. Your listings show the verified badge and rank higher in search results.
        </p>
        <div className="bg-dark-surface border border-green-400/30 rounded-2xl px-5 py-4 w-full flex items-center gap-3">
          <span className="text-2xl">🏅</span>
          <div>
            <p className="text-green-400 font-semibold text-sm">Verified Provider</p>
            <p className="text-dark-subtext text-xs mt-0.5">Verified by Findr team</p>
          </div>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="mt-6 w-full py-4 rounded-2xl bg-dark-surface border border-dark-border text-dark-text font-semibold text-sm"
        >
          Go Back
        </button>
      </div>
    );
  }

  // Submitted confirmation screen
  if (submitted) {
    return (
      <div className="min-h-screen bg-dark-bg flex flex-col items-center justify-center px-6 pb-24 pt-12">
        <div className="w-20 h-20 rounded-full bg-brand-purple/20 border-2 border-brand-purple flex items-center justify-center text-4xl mb-5">
          🕐
        </div>
        <h2 className="text-dark-text text-2xl font-bold mb-2" style={{ letterSpacing: '-0.5px' }}>
          Submitted!
        </h2>
        <p className="text-dark-subtext text-sm text-center leading-relaxed mb-6">
          We'll review your documents and get back to you within <span className="text-dark-text font-semibold">24 hours</span>.
        </p>
        <div className="bg-dark-surface border border-dark-border rounded-2xl px-5 py-4 w-full">
          {BENEFITS.map((b, i) => (
            <div key={i} className="flex items-start gap-2.5 py-2 border-b border-dark-border last:border-b-0">
              <span className="text-brand-purple mt-0.5">✓</span>
              <p className="text-dark-subtext text-sm">{b}</p>
            </div>
          ))}
        </div>
        <button
          onClick={() => navigate(-1)}
          className="mt-6 w-full py-4 rounded-2xl bg-brand-gradient text-white font-bold text-base"
        >
          Done
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-bg pb-24">
      {/* Hidden inputs */}
      <input ref={idFrontRef} type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => { setIdFront(e.target.files?.[0] || null); e.target.value = ''; }} />
      <input ref={idBackRef} type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => { setIdBack(e.target.files?.[0] || null); e.target.value = ''; }} />
      <input ref={portfolioRef} type="file" accept="image/*,.pdf,.mp4" multiple className="hidden" onChange={handlePortfolioFiles} />

      {/* Header */}
      <div className="px-4 pt-12 pb-4 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 flex items-center justify-center rounded-xl bg-dark-surface border border-dark-border"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-dark-text">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h1 className="text-xl font-bold text-dark-text" style={{ letterSpacing: '-0.5px' }}>Get Verified</h1>
      </div>

      <div className="px-4 flex flex-col gap-5">

        {/* Explanation banner */}
        <div className="bg-brand-gradient rounded-2xl px-5 py-4">
          <p className="text-white font-semibold text-base mb-1">Stand out from the crowd</p>
          <p className="text-white/80 text-sm leading-relaxed">
            Verified providers get a badge and rank higher in search results, helping you win more clients.
          </p>
        </div>

        {/* Benefits list */}
        <div className="bg-dark-surface border border-dark-border rounded-2xl px-4 py-1">
          {BENEFITS.map((b, i) => (
            <div key={i} className="flex items-start gap-3 py-3.5 border-b border-dark-border last:border-b-0">
              <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-green-400 text-xs font-bold">✓</span>
              </div>
              <p className="text-dark-text text-sm leading-snug">{b}</p>
            </div>
          ))}
        </div>

        {/* Government ID upload */}
        <div>
          <p className="text-dark-subtext text-xs font-medium uppercase tracking-wide mb-2">
            Government ID
          </p>
          <div className="flex gap-3">
            {/* Front */}
            <button
              onClick={() => idFrontRef.current?.click()}
              className={`flex-1 h-28 rounded-2xl border-2 flex flex-col items-center justify-center gap-1.5 transition-all ${
                idFront
                  ? 'border-green-400 bg-green-400/5'
                  : 'border-dashed border-dark-border bg-dark-surface'
              }`}
            >
              {idFront ? (
                <>
                  <span className="text-2xl">✅</span>
                  <p className="text-green-400 text-xs font-medium truncate max-w-[90px]">{idFront.name}</p>
                  <p className="text-dark-subtext text-xs">Front</p>
                </>
              ) : (
                <>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6B6B7A" strokeWidth="1.5">
                    <rect x="3" y="6" width="18" height="13" rx="2" />
                    <circle cx="8" cy="11" r="2" />
                    <path d="M14 10h4M14 13h2" />
                  </svg>
                  <p className="text-dark-subtext text-xs">Front *</p>
                </>
              )}
            </button>

            {/* Back */}
            <button
              onClick={() => idBackRef.current?.click()}
              className={`flex-1 h-28 rounded-2xl border-2 flex flex-col items-center justify-center gap-1.5 transition-all ${
                idBack
                  ? 'border-green-400 bg-green-400/5'
                  : 'border-dashed border-dark-border bg-dark-surface'
              }`}
            >
              {idBack ? (
                <>
                  <span className="text-2xl">✅</span>
                  <p className="text-green-400 text-xs font-medium truncate max-w-[90px]">{idBack.name}</p>
                  <p className="text-dark-subtext text-xs">Back</p>
                </>
              ) : (
                <>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6B6B7A" strokeWidth="1.5">
                    <rect x="3" y="6" width="18" height="13" rx="2" />
                    <path d="M7 11h10M7 14h6" />
                  </svg>
                  <p className="text-dark-subtext text-xs">Back (optional)</p>
                </>
              )}
            </button>
          </div>
          <p className="text-dark-subtext text-xs mt-1.5">Accepted: Passport, Civil ID, Driver's License</p>
        </div>

        {/* Portfolio samples */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-dark-subtext text-xs font-medium uppercase tracking-wide">
              Portfolio Samples
            </p>
            <p className="text-dark-subtext text-xs">{portfolioFiles.length}/5</p>
          </div>
          <button
            onClick={() => portfolioRef.current?.click()}
            disabled={portfolioFiles.length >= 5}
            className="w-full h-20 rounded-2xl border-2 border-dashed border-dark-border bg-dark-surface flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6B6B7A" strokeWidth="1.5">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span className="text-dark-subtext text-sm">
              {portfolioFiles.length === 0 ? 'Upload portfolio files' : 'Add more files'}
            </span>
          </button>

          {portfolioFiles.length > 0 && (
            <div className="flex flex-col gap-1.5 mt-2">
              {portfolioFiles.map((f, i) => (
                <div key={i} className="flex items-center gap-2 bg-dark-surface border border-dark-border rounded-xl px-3 py-2">
                  <span className="text-base">📎</span>
                  <p className="flex-1 text-dark-text text-xs truncate">{f.name}</p>
                  <button
                    onClick={() => setPortfolioFiles((prev) => prev.filter((_, j) => j !== i))}
                    className="text-dark-subtext text-xs hover:text-red-400"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Portfolio URL */}
        <div>
          <label className="text-dark-subtext text-xs font-medium mb-1.5 block uppercase tracking-wide">
            Portfolio URL / Website <span className="normal-case font-normal">(optional)</span>
          </label>
          <input
            type="url"
            value={portfolioUrl}
            onChange={(e) => setPortfolioUrl(e.target.value)}
            placeholder="https://yourportfolio.com"
            className="w-full bg-dark-surface border border-dark-border rounded-xl px-4 py-3 text-dark-text text-sm outline-none focus:border-brand-purple placeholder:text-dark-muted"
          />
        </div>

        {/* Privacy note */}
        <div className="bg-dark-surface border border-dark-border rounded-2xl px-4 py-3 flex items-start gap-2.5">
          <span className="text-base mt-0.5">🔒</span>
          <p className="text-dark-subtext text-xs leading-relaxed">
            Your documents are encrypted and only viewed by the Findr trust & safety team. They will never be shared with clients or third parties.
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full py-4 rounded-2xl bg-brand-gradient text-white font-bold text-base disabled:opacity-60 transition-opacity"
        >
          {submitting ? 'Submitting...' : 'Submit for Review'}
        </button>

      </div>
    </div>
  );
}
